import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";

import { useAuthQuery } from "@stadium/services/apollo-client";
import { useFeatureFlag } from "@stadium/services/launch-darkly";

import Multimedia from "@sharedComponents/multimedia";
import ChooseMoreSwagBanner from "@sharedComponents/banner/choose-more-swag";
import { InternalButton } from "@sharedComponents/buttons";

import {
  getAllMenuTaxons,
  getSelectedMenuItem,
  getParentCategoryFromCategory,
  getTaxonIdsRecursively,
  getProductCountryMapping,
} from "@helpers/menu";
import { getLockerShopESQueryVars } from "@helpers/store";
import { convertPrice } from "@helpers/currency";
import { hyphenatedStringToReadable } from "@stadium/helpers/string";
import { isGlobalCountry } from "@stadium/helpers/country/utils";
import { getUserDesignedStoreIds } from "@helpers/products/search";

import {
  getMenu,
  getMenuAllChildren,
  getMenuCodeMapping,
  getMenuIsLoaded,
} from "@store/selectors/menu";
import { getCountry } from "@store/selectors/country";
import {
  getIsLockerShop,
  getIsLegacyLockerShop,
  getStoreBalance,
  getStoreCloneId,
  getStoreID,
  getStoreParentId,
  getStoreSyncedToES,
  getCommonElasticSearchStoreVariables,
  getIsPricelessShop,
} from "@store/selectors/store";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { ProductListingContext } from "@components/store/products/listing/listing-provider";
import Product from "@sharedComponents/cards/product";
import ProductListingHeader from "@components/store/products/listing-header";
import ListingPagination from "@components/store/products/listing-pagination";
import { GetStartedBanner } from "@components/preview-catalog/banners";
import { useStoreContext } from "@components/store/provider";
import ScrollToTopBtn from "@stadium/ui/src/components/scroll-to-top-button";
import ScrollToTopOnMount from "@stadium/ui/src/components/scroll-to-top";
import AddProductsToCatalogBanner from "@components/create-swag/product-listing/add-products-to-catalog-banner";

import { getStoreCurrencyCode } from "@selectors/store";
import { getPricelessProductFetchVariables } from "@selectors/product";
import { getConversionRates } from "@stadium/redux-utils/selectors/configSelector";

import { useConfig } from "@stadium/hooks/lib/use-config";
import useShowNewHeader from "@hooks/use-show-new-header";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import useSearchProductList from "@hooks/search/use-fetch-base-product-list";
import useSetFilters from "@hooks/search/use-set-filter";
import { useShopAnalyticsContext } from "@hooks/analytics/use-shop-analytics-context";
import { useCreateShopContext } from "@contexts/create-shop-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";
import usePopulateAutomatedCatalog from "@hooks/automated-catalog/use-populate-automated-catalog";
import useSelectedCountriesIsos from "@hooks/search/use-selected-countries-isos";

import { ButtonLoader } from "@stadium/ui/src/components/loaders/loaders";

import { fetchUserDesignedStoreConfig } from "@graphql/queries/stores/auth-based-params";

import notFoundImg from "@images/svg/no-result.svg";

import { US_CURRENCY_CODE } from "@constants/store";
import {
  ADDITIONAL_ON_DEMAND_FILTER_TYPE,
  BULK_ITEM_FILTER_TYPE,
  CORE_ON_DEMAND_FILTER_TYPE,
  ECO_FRIENDLY_FILTER,
  ON_DEMAND_FILTER_TYPE,
  RECOMMENDED_SORT_ITEM,
  SHIPPED_FROM_STADIUM_FILTER,
  SUSTAINABLE_FILTER,
  VOLUME_DISCOUNT_FILTER,
  LOCKER_INVENTORY_FILTER_TYPE,
} from "@constants/products/listing";
import { WAREHOUSE_PRODUCT } from "@constants/products/detail";
import { addSwagToShopBannerData } from "@constants/banner";
import { MY_DESIGNED_SWAG_LOCKER, PREDEFINED_MENU_ITEMS } from "@constants/menu";
import { ALL_PRODUCTS, ALL_SUBCATEGORY_CODE } from "@constants/routes";
import { LISTING_SECTION_REF } from "@components/store/products/listing";
import { ANALYTICS_EVENT_ACTION_CHANGED } from "@stadium/services/analytics/constants/default";
import { ANALYTICS_TARGET_PAGINATION } from "@constants/analytics/filter";

import ListingScrollTracker from "@components/store/products/listing-scroll-tracker";
import ListingLoader from "@components/store/products/listing/listing/loader";

import styles from "../main.module.scss";

const AddSwagToShopBanner = dynamic(() => import("@sharedComponents/banner/add-swag-to-shop"));

const emptyObj = {};
const emptyArr = [];
const BANNER_POSITION = 8;

const ProductListing = ({ showLeftFilter, showLeftPanel, topBannerPresent, isKitFlow = false }) => {
  const pathname = usePathname();
  const { isReady } = useFeatureFlag();

  const {
    isPreviewCatalogMode,
    onlyHasAllProductsMenu,
    category: categoryCode,
    subCategory: subCategoryCode,
    menuIdentifier,
  } = useStoreContext();

  const shopAnalyticsContext = useShopAnalyticsContext();

  const showNewHeader = useShowNewHeader();

  const { parentCategoryCode, activeCategoryCode } = getParentCategoryFromCategory(categoryCode);

  const {
    currentDietaryRestrictionsIds,
    currentPage,
    currentPriceRangeIds,
    currentColorOptionValueIds,
    currentSizeOptionValueIds,
    currentCountryIds,
    currentSortBy,
    setPage,
    setTotalProducts,
    customizable,
    setIsFetchingProducts,
    flattenedBrandValueIds,
    selectedProductionDays,
    isFetchingProducts,
    selectedItemFeatures,
    currentDecorationMethods,
    perPage,
    setPerPage,
    selectedItemTypes,
    hasFiltersApplied,
    designedItemTypes,
  } = useContext(ProductListingContext);

  const { selectedCountryIsos } = useSelectedCountriesIsos({ currentCountryIds });

  const { isNewShopCreationFlow } = useCreateShopContext();
  const {
    isSwagCreationShopFlow,
    preferredColorNames,
    draftSwagData,
    currentUserConfig,
    isLoadingDraftSwag,
  } = useCreateSwagContext();
  const { isDesigner = false } = currentUserConfig || {};

  const { addProductToAutomatedCatalog, isPopulatingDraftAutomatedCatalog } =
    usePopulateAutomatedCatalog();

  const { previewMode, getStoreUpdatedAt } = usePreviewModeContext();
  const storeBalance = useSelector(getStoreBalance);
  const [childCategory, setChildCategory] = useState();
  const [parentCategory, setParentCategory] = useState();
  const syncedToEs = useSelector(getStoreSyncedToES);
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const menu = useSelector(getMenu);
  const menuCodeMapping = useSelector(getMenuCodeMapping);
  const country = useSelector(getCountry);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const conversionRates = useConfig(getConversionRates);
  const storeCloneId = useSelector(getStoreCloneId);
  const productCardClass = styles.colCard;
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const isLockerShop = useSelector(getIsLockerShop);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const allChildren = useSelector(getMenuAllChildren);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const isMenuLoaded = useSelector(getMenuIsLoaded);

  const showFiltersDropdown = !showLeftFilter && !isNewShopCreationFlow;
  const { isDesktopDevice, isMobileDevice } = useContext(CurrentDeviceContext);

  const { data: designedStoreConfig, loading: isFetchingDesignedStore } = useAuthQuery(
    fetchUserDesignedStoreConfig,
    {
      skip: !(isLockerShop || isKitFlow) || !isReady,
    },
  );

  const updatedDesignedStoreConfig = useMemo(() => {
    const {
      allLockerSwagList = [],
      allOnDemandSwagList = [],
      lockerSwagList = [],
      onDemandSwagList = [],
    } = designedStoreConfig?.user || {};

    return {
      allLockerSwagIds: allLockerSwagList.map(({ id }) => +id),
      allOnDemandSwagIds: allOnDemandSwagList.map(({ id }) => +id),
      lockerSwagIds: lockerSwagList.map(({ id }) => +id),
      onDemandSwagIds: onDemandSwagList.map(({ id }) => +id),
    };
  }, [designedStoreConfig]);

  useEffect(() => {
    const parentItem = getSelectedMenuItem(menuCodeMapping[parentCategoryCode], activeCategoryCode);
    const childItem = parentItem && getSelectedMenuItem(parentItem, subCategoryCode);

    if (parentItem?.id !== parentCategory?.id) {
      setParentCategory(parentItem);
    }
    if (childItem?.id !== childCategory?.id) {
      setChildCategory(childItem);
    }
  }, [categoryCode, menu, parentCategory, childCategory, subCategoryCode]);

  const { taxonIds, withSwag } = useMemo(() => {
    const menuLinkedResources =
      subCategoryCode === ALL_SUBCATEGORY_CODE && parentCategory
        ? getTaxonIdsRecursively(parentCategory)
        : getTaxonIdsRecursively(childCategory);

    const ids = menuLinkedResources.map((resource) => +resource?.id);
    const withSwag = menuLinkedResources.some((resource) => resource?.forSwag);
    const newTaxonIds = ids.filter((id) => id);

    return { taxonIds: newTaxonIds.length ? newTaxonIds : emptyArr, withSwag };
  }, [parentCategory?.id, childCategory?.id, menu, subCategoryCode]);

  const productCountryMap = getProductCountryMapping(menu.menuItems);

  const budget = parseInt(
    convertPrice({
      value: storeBalance || 0,
      from: storeCurrencyCode,
      to: US_CURRENCY_CODE,
      conversionRates,
    }),
  );

  const designedStoreIdsForListing = useMemo(
    () =>
      getUserDesignedStoreIds({
        userConfig: updatedDesignedStoreConfig,
        selectedItemTypes,
        designedItemTypes,
        isLockerShop,
      }),
    [selectedItemTypes, designedItemTypes, updatedDesignedStoreConfig, isLockerShop],
  );

  const { code = "" } = parentCategory || emptyObj;
  const isMyDesignedSwag = code === MY_DESIGNED_SWAG_LOCKER;

  const queryVariables = () => {
    const selectedCountryIso = country.iso?.toLowerCase();
    const isCoreOnDemandFilterSelected = selectedItemTypes.includes(CORE_ON_DEMAND_FILTER_TYPE.id);
    const isAdditionalOnDemandFilterSelected = selectedItemTypes.includes(
      ADDITIONAL_ON_DEMAND_FILTER_TYPE.id,
    );
    const noMinQuantity =
      (isCoreOnDemandFilterSelected && isAdditionalOnDemandFilterSelected) ||
      selectedItemTypes.includes(ON_DEMAND_FILTER_TYPE.id);
    const bulkProducts = selectedItemTypes.includes(BULK_ITEM_FILTER_TYPE.id);
    const hasTierPricing = selectedItemTypes.includes(VOLUME_DISCOUNT_FILTER.id);
    const coreOnDemand = !noMinQuantity && isCoreOnDemandFilterSelected;
    const additionalOnDemand = !noMinQuantity && isAdditionalOnDemandFilterSelected;
    const ecoFriendly = selectedItemFeatures?.some((id) => id === ECO_FRIENDLY_FILTER.id);
    const sustainable = selectedItemFeatures?.some((id) => id === SUSTAINABLE_FILTER.id);
    const shippedFromStadium = selectedItemFeatures?.some(
      (id) => id === SHIPPED_FROM_STADIUM_FILTER.id,
    );
    const isGlobal = isGlobalCountry(country);
    const isSortByRecommended = currentSortBy === RECOMMENDED_SORT_ITEM.id;

    const countryCode = isGlobal ? "" : selectedCountryIso;
    const variables = {
      storeId: isKitFlow ? storeId : storeParentId,
      designedStoreIds: [storeId],
      globalZone: isGlobal,
      ...(menuIdentifier ? { menuIdentifier, withSwag } : { taxonIds, withSwag }),
      productionDays: selectedProductionDays,
      budget,
      page: +currentPage,
      perPage,
      sortBy: currentSortBy,
      dietaryRestrictionIds: currentDietaryRestrictionsIds.map((id) => +id),
      brandIds: flattenedBrandValueIds,
      priceKeys: currentPriceRangeIds,
      skipWarehouse: !syncedToEs && !!storeCloneId,
      color: currentColorOptionValueIds,
      size: currentSizeOptionValueIds,
      storeUpdatedAt: getStoreUpdatedAt(),
      showVariantsAsProducts: true,
      noMinQuantity,
      coreOnDemand,
      additionalOnDemand,
      bulkProducts,
      ecoFriendly,
      printTypes: currentDecorationMethods,
      ...(isKitFlow && { bulkAvailable: false }),
      withOutOfStock: isKitFlow,
      ...(shippedFromStadium && { shippedFromStadium }),
      ...(sustainable && { sustainable }),
      ...commonESVariables,
      ...getLockerShopESQueryVars({
        isLockerShop: isLegacyLockerShop,
      }),
      ...pricelessVariables,
    };

    // to add variable only when it has tier pricing, else skip it
    if (hasTierPricing) {
      variables.hasTierPricing = true;
    }

    if (!isSwagCreationShopFlow) {
      variables.countryCode = countryCode;
    } else {
      variables.countryCode = "";
    }

    if (!menuIdentifier && (!isLockerShop || isSortByRecommended)) {
      variables.menuId = +menu.id;
      variables.menuItemId = +childCategory?.id || +parentCategory?.id;
    }

    const { allMenuTaxonIds, anyMenuWithSwag } = getAllMenuTaxons(allChildren);

    const isAllTab = code === ALL_PRODUCTS;
    if (!menuIdentifier && isAllTab) {
      variables.taxonIds = allMenuTaxonIds;
      variables.withSwag = anyMenuWithSwag;
    }
    if (isKitFlow) {
      if (selectedItemTypes.includes(LOCKER_INVENTORY_FILTER_TYPE.id)) {
        variables.type = WAREHOUSE_PRODUCT;
      }
    }

    if ((isLockerShop && isMyDesignedSwag) || isKitFlow) {
      variables.designedStoreIds = designedStoreIdsForListing;
    }

    if (isLockerShop) {
      variables.optimizedSearch = true;
      variables.withSwag = true;
      if (isMyDesignedSwag) {
        variables.optimizedSearch = false;
        variables.taxonIds = allMenuTaxonIds;
        variables.storeId = null;
      }
    }

    if (isSwagCreationShopFlow) {
      variables.color = preferredColorNames?.map(({ presentation }) => presentation) || [];
      variables.countryCodes = selectedCountryIsos;
      variables.includeNonColorProducts = true;
      variables.designer = isDesigner;
    }

    return variables;
  };

  const {
    loading,
    fetchedProducts,
    totalProducts,
    aggregations,
    getProductList,
    productsInventory,
    isInventoryFetching,
  } = useSearchProductList({ skipInventoryFetching: !isKitFlow });

  const skipFetchIfNoStoreIds =
    (isKitFlow || isMyDesignedSwag) && !designedStoreIdsForListing?.length;

  const preferredColorPresentationsDependencyKey = preferredColorNames
    ?.map(({ presentation }) => presentation)
    .join();
  const selectedCountryIsosDependencyKey = selectedCountryIsos?.join();

  useEffect(() => {
    const variables = queryVariables();

    if (
      (!onlyHasAllProductsMenu || menuIdentifier) &&
      isMenuLoaded &&
      !isFetchingDesignedStore &&
      (variables.taxonIds?.length || variables.menuIdentifier) &&
      !skipFetchIfNoStoreIds &&
      !(isSwagCreationShopFlow && isLoadingDraftSwag)
    ) {
      getProductList({
        variables,
      });
    }
  }, [
    isMenuLoaded,
    taxonIds,
    currentPriceRangeIds,
    currentDietaryRestrictionsIds,
    currentColorOptionValueIds,
    currentSizeOptionValueIds,
    currentDecorationMethods,
    flattenedBrandValueIds,
    currentSortBy,
    currentPage,
    customizable,
    parentCategory,
    commonESVariables,
    perPage,
    pricelessVariables,
    getProductList,
    selectedItemTypes,
    selectedItemFeatures,
    onlyHasAllProductsMenu,
    menu,
    selectedProductionDays,
    isFetchingDesignedStore,
    designedItemTypes,
    designedStoreIdsForListing,
    isSwagCreationShopFlow,
    preferredColorPresentationsDependencyKey,
    selectedCountryIsosDependencyKey,
    isDesigner,
    menuIdentifier,
    isLoadingDraftSwag,
    skipFetchIfNoStoreIds
  ]);

  useSetFilters({ loading, aggregations });

  useEffect(() => {
    setTotalProducts(totalProducts);
  }, [totalProducts]);

  useEffect(() => {
    setIsFetchingProducts(loading);
  }, [loading]);

  const addSwagToShopBannerPosition = useMemo(() => {
    return fetchedProducts.length > BANNER_POSITION
      ? BANNER_POSITION - 1
      : fetchedProducts.length - 1;
  }, [fetchedProducts]);

  const displayBannerVisible = useMemo(
    () =>
      previewMode &&
      (childCategory ? !!childCategory?.swagBannerAddable : !!parentCategory?.swagBannerAddable),
    [childCategory, parentCategory, previewMode],
  );

  const renderAddSwagToShopBanner = () => (
    <AddSwagToShopBanner key={addSwagToShopBannerData.name} data={addSwagToShopBannerData} />
  );

  const noProductsView = () => {
    return (
      <div className={styles.noProductsView}>
        <Multimedia
          src={notFoundImg}
          type="image"
          width={138}
          height={138}
          alt="No results found"
        />
        <div className={styles.noResultFound}>
          {isNewShopCreationFlow && isPricelessShop ? (
            <>
              Looks like there are no products in this price range.{" "}
              <span className={styles.resetFilter}>
                Adjust your price range to see more options.
              </span>
            </>
          ) : (
            <>
              No results found under <strong>{childCategory?.name || parentCategory?.name}</strong>.
              <span className={styles.resetFilter}>
                {" "}
                Try resetting your filters
                {isSwagCreationShopFlow ? " and preferred swag colors" : ""}.
              </span>
            </>
          )}
        </div>
      </div>
    );
  };
  const numOfTilesToShowCatalogBanner = isDesktopDevice ? 23 : 11;

  const renderProducts = (products, startIndex = 0) => {
    if (!products?.length) return "";
    return (
      <article
        className={`${styles.rowWrapper} ${showLeftPanel ? styles.productCardsWrapper : styles.productCardsWrapperFive
          } ${isSwagCreationShopFlow ? styles.swagProductCardsWrapper : ""}`}
      >
        {products.map((product, index) => {
          const productQtyMapping = productsInventory[product?.id]?.total || emptyObj;

          const { productId, storeProduct } = product;
          const productCatalogId = `${productId}-${storeProduct?.catalogStoreId || 0}`;
          const country = productCountryMap[productCatalogId];

          return (
            <Fragment key={`product-${product.id}`}>
              <Product
                data={product}
                key={product.id}
                source="search"
                productTileClass={`${styles.productListingPageOnly} white-base-tile`}
                className={productCardClass}
                imageLoadingStrategy="lazy"
                showInventoryStatus={!isInventoryFetching && isKitFlow}
                productQtyMapping={productQtyMapping}
                country={country}
                eventLocationAnalytics={{
                  track: true,
                  eventProperties: {
                    ...shopAnalyticsContext,
                    product_id: product.id,
                    product_name: product.name,
                    position_in_list: startIndex + index + 1,
                  },
                }}
              />
            </Fragment>
          );
        })}
      </article>
    );
  };

  const bannerPosition = (() => {
    if (displayBannerVisible || isKitFlow) {
      return addSwagToShopBannerPosition;
    }
    if (isPreviewCatalogMode) {
      return numOfTilesToShowCatalogBanner;
    }
    return fetchedProducts.length - 1;
  })();

  const productsAboveBanner = fetchedProducts.slice(0, bannerPosition + 1);
  const productsBelowBanner = fetchedProducts.slice(bannerPosition + 1);

  const isNoProductsAvailable =
    (onlyHasAllProductsMenu && !menuIdentifier) ||
    isEmpty(fetchedProducts) ||
    skipFetchIfNoStoreIds;

  const renderProductsListing = () => {
    if (
      (!onlyHasAllProductsMenu &&
        !menuIdentifier &&
        taxonIds.length === 0 &&
        !PREDEFINED_MENU_ITEMS.includes(parentCategory?.code)) ||
      loading ||
      isFetchingDesignedStore
    ) {
      return <ListingLoader showLeftPanel={showLeftPanel} />;
    }

    if (isNoProductsAvailable) {
      if (isKitFlow) {
        return hasFiltersApplied ? noProductsView() : <ChooseMoreSwagBanner hasNoProducts />;
      }
      return noProductsView();
    }
    return (
      <ListingScrollTracker baseEventProperties={shopAnalyticsContext}>
        {renderProducts(productsAboveBanner, 0)}
        {isKitFlow && <ChooseMoreSwagBanner />}
        {displayBannerVisible && renderAddSwagToShopBanner()}
        {isPreviewCatalogMode && <GetStartedBanner />}
        {renderProducts(productsBelowBanner, productsAboveBanner.length)}
      </ListingScrollTracker>
    );
  };

  const title = (() => {
    if (menuIdentifier) {
      return hyphenatedStringToReadable(menuIdentifier);
    }
    if (!showNewHeader) {
      return childCategory?.name;
    }
    if (isMobileDevice || !fetchedProducts?.length || parentCategory?.code == ALL_PRODUCTS) {
      return "";
    }

    if (!topBannerPresent && subCategoryCode === ALL_SUBCATEGORY_CODE) {
      return parentCategory?.name;
    }

    if (!childCategory?.children?.length && childCategory?.depth === 2) {
      return childCategory?.name;
    }
  })();

  const addAllCatalogCategoryName = useMemo(
    () => childCategory?.name || parentCategory?.name || "Products",
    [childCategory?.name, parentCategory?.name],
  );

  const renderAddAllToCatalogCta = () => {
    if (!isSwagCreationShopFlow) return null;

    if (parentCategory?.code === ALL_PRODUCTS) {
      return null;
    }

    const addAllDisabled =
      loading ||
      isFetchingDesignedStore ||
      !totalProducts ||
      !draftSwagData?.number ||
      skipFetchIfNoStoreIds;

    return (
      <>
        <InternalButton
          styleType="primary-outline"
          className={styles.addAllCatalogButton}
          onClick={() => addProductToAutomatedCatalog({ filters: queryVariables() })}
          disabled={addAllDisabled || isPopulatingDraftAutomatedCatalog}
        >
          {isPopulatingDraftAutomatedCatalog ? (
            <ButtonLoader wrapperClass={styles.addAllCatalogSpinner} />
          ) : (
            <>
              Add all{" "}
              <span className={styles.addAllCatalogCategory}>{addAllCatalogCategoryName}</span>
            </>
          )}
        </InternalButton>
      </>
    );
  };

  return (
    <section ref={LISTING_SECTION_REF} className={styles.pageSection}>
      <ScrollToTopOnMount key={pathname} />
      <ProductListingHeader
        totalProducts={isNoProductsAvailable ? 0 : totalProducts}
        currentPage={currentPage}
        perPage={perPage}
        showFilters={showFiltersDropdown}
        showSortDropdown={!isNewShopCreationFlow}
        title={title}
        isFetchingProducts={loading}
        renderAddAllToCatalogCta={renderAddAllToCatalogCta}
      />
      {isSwagCreationShopFlow && <AddProductsToCatalogBanner />}

      <ScrollToTopBtn />
      {renderProductsListing()}
      {!isFetchingProducts && (
        <ListingPagination
          total={isNoProductsAvailable ? 0 : totalProducts}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={setPage}
          className="pagination"
          setPerPage={setPerPage}
          showPerPageDropdown
          eventLocationAnalytics={{
            track: true,
            target: ANALYTICS_TARGET_PAGINATION,
            action: ANALYTICS_EVENT_ACTION_CHANGED,
            eventProperties: shopAnalyticsContext,
          }}
        />
      )}
    </section>
  );
};

export default ProductListing;
